import { useEffect, useRef, useState } from "react";
import { loadMember, searchMembers } from "../../api/members";
import type { ReportConfigurationMemberSearchResult } from "../../types";
import { ChipList } from "../ChipList";

interface MemberSearchPickerProps {
    values: string[];
    onAdd: (value: string) => void;
    onRemove: (value: string) => void;
}

const SEARCH_DELAY_MS = 1000;
const MIN_SEARCH_LENGTH = 3;

function memberInitials(member?: Pick<ReportConfigurationMemberSearchResult, "firstName" | "lastName" | "fullName"> | null) {
    if (!member) return "M";
    const first = member.firstName?.[0] || member.fullName?.trim()?.[0] || "";
    const last = member.lastName?.[0] || "";
    return `${first}${last}`.toUpperCase() || "M";
}

export function MemberSearchPicker({ values, onAdd, onRemove }: MemberSearchPickerProps) {
    const [searchText, setSearchText] = useState("");
    const [results, setResults] = useState<ReportConfigurationMemberSearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectingId, setSelectingId] = useState("");
    const [error, setError] = useState("");
    const [open, setOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const requestSeq = useRef(0);
    const lastQueryKey = useRef("");

    useEffect(() => {
        const trimmed = searchText.trim();
        const canSearch = trimmed.length >= MIN_SEARCH_LENGTH;

        if (!canSearch) {
            requestSeq.current += 1;
            setLoading(false);
            setResults([]);
            setError("");
            setOpen(false);
            lastQueryKey.current = "";
            return;
        }

        const queryKey = trimmed;
        if (queryKey === lastQueryKey.current) {
            if (focused) setOpen(true);
            return;
        }

        const timeoutId = window.setTimeout(async () => {
            const seq = requestSeq.current + 1;
            requestSeq.current = seq;
            lastQueryKey.current = queryKey;
            setLoading(true);
            setError("");
            setOpen(true);

            try {
                const response = await searchMembers(trimmed, "");
                if (requestSeq.current !== seq) return;
                setResults(Array.isArray(response) ? response : []);
            }
            catch {
                if (requestSeq.current !== seq) return;
                setResults([]);
                setError("Member search is unavailable. Please try again.");
            }
            finally {
                if (requestSeq.current === seq) setLoading(false);
            }
        }, SEARCH_DELAY_MS);

        return () => window.clearTimeout(timeoutId);
    }, [focused, searchText]);

    const selectMember = async (member: ReportConfigurationMemberSearchResult) => {
        setSelectingId(member.memberIdEncoded);
        setError("");

        try {
            const detail = await loadMember(member.memberIdEncoded);
            const publicMemberId = detail?.memberId || member.memberId;
            if (publicMemberId) onAdd(publicMemberId);
            setSearchText("");
            setOpen(false);
            setResults([]);
            lastQueryKey.current = "";
        }
        catch {
            setError("Member details could not be loaded.");
            setOpen(true);
        }
        finally {
            setSelectingId("");
        }
    };

    const showEmpty = open && !loading && !error && !results.length && searchText.trim().length >= MIN_SEARCH_LENGTH;
    const showHelp = focused && !open && searchText.trim().length > 0 && searchText.trim().length < MIN_SEARCH_LENGTH;

    return (
        <div className="rc-member-search">
            <div className="rc-member-head">
                <div>
                    <p className="rc-hint">Member IDs</p>
                    {values.length > 0 && <span>{`${values.length} Member${values.length === 1 ? "" : "s"} Excluded`}</span>}
                </div>
            </div>
            <ChipList values={values} onRemove={onRemove} emptyText="No Member Exclusions Added" />
            <div className="rc-member-search-card">
                <label className="rc-member-query">
                    <span>Find Member</span>
                    <span className="rc-member-input-wrap">
                        <span className="material-icons md-18 rc-member-search-icon" aria-hidden="true">person_search</span>
                        <input
                            type="text"
                            value={searchText}
                            placeholder="Search by Name, Member ID, or Phone No.    "
                            onBlur={() => window.setTimeout(() => { setFocused(false); setOpen(false); }, 140)}
                            onChange={(event) => {
                                setSearchText(event.target.value);
                            }}
                            onFocus={() => {
                                setFocused(true);
                                if (results.length || loading || error) setOpen(true);
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Escape") setOpen(false);
                                if (event.key === "Enter" && results[0] && !selectingId) {
                                    event.preventDefault();
                                    selectMember(results[0]);
                                }
                            }}
                        />
                        {!!searchText && !loading && (
                            <button
                                aria-label="Clear member search"
                                className="rc-member-clear"
                                type="button"
                                onClick={() => {
                                    setSearchText("");
                                    setResults([]);
                                    setOpen(false);
                                    setError("");
                                }}
                            >
                                <span className="material-icons md-16" aria-hidden="true">close</span>
                            </button>
                        )}
                        {loading && <span className="material-icons md-16 rc-member-loading" aria-hidden="true">progress_activity</span>}
                    </span>
                </label>
                {showHelp && <div className="rc-member-helper">Type at least {MIN_SEARCH_LENGTH} characters to search.</div>}
                {open && (
                    <div className="rc-member-results" role="listbox">
                        {loading && <div className="rc-member-state"><span className="material-icons md-18" aria-hidden="true">manage_search</span>Searching members...</div>}
                        {error && <div className="rc-member-state error">{error}</div>}
                        {showEmpty && <div className="rc-member-state"><span className="material-icons md-18" aria-hidden="true">person_off</span>No matching members found.</div>}
                        {!loading && !error && results.map((member) => (
                            <button
                                className="rc-member-option"
                                disabled={!!selectingId}
                                key={member.memberIdEncoded || member.memberId}
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => selectMember(member)}
                            >
                                <span className="rc-member-option-avatar" aria-hidden="true">{memberInitials(member)}</span>
                                <span className="rc-member-option-main">
                                    <span className="rc-member-option-title">
                                        <strong>{member.fullName || member.memberId}</strong>
                                        <span>{member.memberId}</span>
                                    </span>
                                    <span className="rc-member-option-grid">
                                        <span><span className="material-icons md-14" aria-hidden="true">calendar_month</span>{member.dobDisplay || "DOB not available"}</span>
                                        <span><span className="material-icons md-14" aria-hidden="true">business</span>{[member.clientName, member.lobName || member.groupCode].filter(Boolean).join(" / ") || "Plan not available"}</span>
                                        <span><span className="material-icons md-14" aria-hidden="true">call</span>{member.primaryContactNo || "N/A"}</span>
                                        <span><span className="material-icons md-14" aria-hidden="true">home</span>{member.physicalAddress || "Address not available"}</span>
                                    </span>
                                    {selectingId === member.memberIdEncoded && <span className="rc-member-selecting">Loading details...</span>}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
